import { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Alert,
} from '@mui/material';

import banner from '../assets/banner.png';
import categories from '../assets/menus/products.json';
import { appConfig } from '../configs/app-config';

export default function ProductList() {
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const matchedCategory = categories.find((c) => c.routeTo === category);
  if(!matchedCategory) {
    return <Navigate to="/" replace />;
  };

  useEffect(() => {
    if (!matchedCategory) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    setLoading(true);
    setError(null);

    import(`../assets/products/${matchedCategory.routeTo}.json`)
      .then((mod) => {
        if (isMounted) {
          console.log(mod.default);
          setProducts(mod.default || []);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(`Unable to load products for "${matchedCategory.name}".`);
          setProducts([]);
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [matchedCategory]);

  if (!matchedCategory) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box sx={{ py: 6 }}>
      <Container>
        {/* HERO BANNER */}
        <Box
          sx={{
            height: { xs: 300, md: 420 },
            backgroundImage: `linear-gradient(
                    rgba(0,0,0,0.45),
                    rgba(0,0,0,0.45)
                  ), url(${banner})`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            display: 'flex',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <Container>
            <Typography variant="h3" color="#fff" fontWeight={700}>
              {matchedCategory.name}
            </Typography>
            <Typography
              variant="h6"
              color="#f5e6d3"
              sx={{ mt: 1, maxWidth: 500 }}
            >
              Delivering freshness, quality, and tradition to your kitchen
            </Typography>
          </Container>
        </Box>

        {/* PAGE HEADER */}
        <Box mb={4}>
          <Typography variant="h4" fontWeight={600}>
            Our {matchedCategory.name}
          </Typography>
          <Typography color="text.secondary" mt={1}>
            High-quality {matchedCategory.name.toLowerCase()} for retail and
            wholesale supply
          </Typography>
        </Box>

        {/* LOADING / ERROR / EMPTY STATES */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ my: 4 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && products.length === 0 && (
          <Alert severity="info" sx={{ my: 4 }}>
            No products found in this category yet.
          </Alert>
        )}

        {/* PRODUCT GRID */}
        {!loading && !error && products.length > 0 && (
          <Grid container spacing={4}>
            {products.map((product, index) => (
              <Grid item xs={12} sm={6} md={4} key={`${product.productTitle}-${index}`}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: '0.3s',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 14px 28px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={`/assets/media/products/${matchedCategory.routeTo}/${product.image}`}
                    alt={product.productTitle}
                    sx={{ objectFit: 'cover' }}
                  />

                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight={600} mb={1}>
                      {product.productTitle}
                    </Typography>

                    <Stack spacing={0.5}>
                      {product.productName && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Botanical Name:</strong> {product.productName}
                        </Typography>
                      )}
                      {product.productSize && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Pack Size:</strong> {product.productSize}
                        </Typography>
                      )}
                      {product.materialSize && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Packaging:</strong> {product.materialSize}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>

                  <Box sx={{ p: 2 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      target='_blank'
                      href={`https://wa.me/${appConfig.appAuthorPhone.replace(/\s+/g, '')}?text=${encodeURIComponent(`Hi!! I am Enquiry for ${product.productTitle}`)}`}
                    >
                      Contact Now
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
}
